import React, { useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import HeroSection from './components/HeroSection';
import PainPointSection from './components/PainPointSection';
import SolutionSection from './components/SolutionSection';
import PreviewSection from './components/PreviewSection';
import TestimonialSection from './components/TestimonialSection';
import BenefitsSection from './components/BenefitsSection';
import CTASection from './components/CTASection';
import NewsletterSection from './components/NewsletterSection';
import ContactForm from './components/ContactForm';
import AdminDashboard from './components/AdminDashboard';
import RedeemPage from './components/RedeemPage';
import SocialProofCounter from './components/SocialProofCounter';
import ExitIntentPopup from './components/ExitIntentPopup';
import BookLandingPage from './pages/BookLandingPage';
import BlogPage from './pages/BlogPage';
import ArticlePage from './pages/ArticlePage';
import UnsubscribePage from './pages/UnsubscribePage';
import TagPage from './pages/TagPage';
import Chatbot from './components/Chatbot';
import LanguagePicker, { useTranslation, t, LanguageProvider } from './components/LanguagePicker';
import { AuthProvider } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import MemberArea from './pages/MemberArea';
import AuthorPage, { AUTHORS, AUTHOR_NAME_TO_SLUG } from './pages/AuthorPage';
import { bookInfo } from './data/mockData';
import { ShoppingCart, Mail, BookOpen, Heart, X, ArrowRight, ChevronDown, ChevronUp, Home } from 'lucide-react';
import { useAnalytics } from './hooks/useAnalytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterPopup = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    setMessage('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscribe`, { email, source: 'popup' });
      setMessage(response.data.message);
      setSuccess(true);
      setEmail('');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Terjadi kesalahan.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="newsletter-popup-overlay" onClick={onClose} data-testid="newsletter-popup-overlay">
      <div className="newsletter-popup" onClick={(e) => e.stopPropagation()} data-testid="newsletter-popup">
        <button className="popup-close" onClick={onClose} data-testid="newsletter-popup-close"><X size={20} /></button>
        <Mail size={32} className="popup-icon" />
        <h3 className="popup-title">Subscribe Newsletter</h3>
        <p className="popup-desc">Dapatkan update artikel terbaru, event penting, dan preview bab gratis langsung ke inbox Anda.</p>
        {!success ? (
          <form onSubmit={handleSubmit} className="popup-form" data-testid="newsletter-popup-form">
            <input
              type="email"
              placeholder="Masukkan email Anda"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              data-testid="newsletter-popup-email"
            />
            <button type="submit" disabled={isSubmitting} data-testid="newsletter-popup-submit">
              {isSubmitting ? 'Mendaftar...' : 'Berlangganan'} <ArrowRight size={18} />
            </button>
          </form>
        ) : (
          <div className="popup-success" data-testid="newsletter-popup-success">
            <p>{message}</p>
          </div>
        )}
        {!success && message && <p className="popup-message">{message}</p>}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const { trackConversion } = useAnalytics();
  const [showAuthorsAll, setShowAuthorsAll] = useState(false);
  const [showNewsletterPopup, setShowNewsletterPopup] = useState(false);
  useTranslation();

  const handleEmailSubmit = (email) => {
    console.log('Email submitted:', email);
  };

  const scrollToNewsletter = () => {
    document.querySelector('.newsletter-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBuyNow = () => {
    trackConversion('buy_now');
    window.open(bookInfo.googleBooksUrl, '_blank');
  };

  return (
    <>
      {/* Header */}
      <header className="main-header">
        <div className="header-container">
          <div className="header-logo">
            <BookOpen size={32} />
            <div className="logo-text">
              <span className="logo-title">Atomic Habits</span>
              <span className="logo-subtitle">for Sharia Bankers</span>
            </div>
          </div>
          <nav className="header-nav">
            <Link to="/" className="nav-link" data-testid="nav-home">
              <Home size={18} />
              {t('lp_nav_home')}
            </Link>
            <button onClick={scrollToNewsletter} className="nav-link">
              <Mail size={18} />
              {t('lp_nav_newsletter')}
            </button>
            <button onClick={handleBuyNow} className="btn-header-cta">
              <ShoppingCart size={18} />
              {t('lp_nav_buy')}
            </button>
            <LanguagePicker />
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <HeroSection onEmailSubmit={handleEmailSubmit} />
        <SocialProofCounter />
        <PainPointSection />
        <SolutionSection />
        <PreviewSection />
        <TestimonialSection />
        <BenefitsSection />
        <CTASection />
        <NewsletterSection />
        <ContactForm />
      </main>

      {/* Footer */}
      <footer className="main-footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-column">
              <h4 className="footer-title">{t('lp_footer_about')}</h4>
              <p className="footer-text">
                <strong>{bookInfo.title}</strong><br />
                {bookInfo.subtitle}
              </p>
              <p className="footer-text">
                {bookInfo.pages} {t('lp_footer_pages_suffix')}<br />
                Penerbit: {bookInfo.publisher}<br />
                ISBN: {bookInfo.isbn}
              </p>
            </div>

            <div className="footer-column">
              <h4 className="footer-title">{t('lp_footer_authors')}</h4>
              <ul className="footer-list">
                {bookInfo.authors.slice(0, 3).map((author, index) => (
                  <li key={index}>
                    {AUTHOR_NAME_TO_SLUG[author] ? (
                      <Link to={`/penulis/${AUTHOR_NAME_TO_SLUG[author]}`} className="footer-author-link" data-testid={`footer-author-${index}`}>{author}</Link>
                    ) : author}
                  </li>
                ))}
                {!showAuthorsAll ? (
                  <li>
                    <button className="footer-more-btn" onClick={() => setShowAuthorsAll(true)} data-testid="show-more-authors">
                      <ChevronDown size={14} /> + {bookInfo.authors.length - 3} {t('lp_footer_more')}
                    </button>
                  </li>
                ) : (
                  <>
                    {bookInfo.authors.slice(3).map((author, index) => (
                      <li key={index + 3} className="author-reveal">
                        {AUTHOR_NAME_TO_SLUG[author] ? (
                          <Link to={`/penulis/${AUTHOR_NAME_TO_SLUG[author]}`} className="footer-author-link" data-testid={`footer-author-${index + 3}`}>{author}</Link>
                        ) : author}
                      </li>
                    ))}
                    <li>
                      <button className="footer-more-btn" onClick={() => setShowAuthorsAll(false)} data-testid="show-less-authors">
                        <ChevronUp size={14} /> {t('lp_footer_less')}
                      </button>
                    </li>
                  </>
                )}
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-title">{t('lp_footer_links')}</h4>
              <ul className="footer-list">
                <li>
                  <a href={bookInfo.googleBooksUrl} target="_blank" rel="noopener noreferrer">
                    {t('lp_footer_buy_gbooks')}
                  </a>
                </li>
                <li>
                  <button onClick={() => setShowNewsletterPopup(true)} data-testid="footer-subscribe-btn">{t('lp_footer_subscribe')}</button>
                </li>
                <li>
                  <a href="#contact" onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('.contact-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}>
                    {t('lp_footer_contact_us')}
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-title">{t('lp_footer_connect')}</h4>
              <p className="footer-text">
                Email: info@pakdeheru.com<br />
                WhatsApp: +62 811-800-7799
              </p>
              <div className="footer-social">
                <a href="https://www.instagram.com/pakdeheruu/" target="_blank" rel="noopener noreferrer" className="social-link">Instagram</a>
                <a href="https://www.linkedin.com/in/herusidik" target="_blank" rel="noopener noreferrer" className="social-link">LinkedIn</a>
                <a href="https://www.facebook.com/pakdeheruu/" target="_blank" rel="noopener noreferrer" className="social-link">Facebook</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copyright">
              {t('lp_footer_copyright')}
            </p>
            <p className="footer-tagline">
              <Heart size={16} className="heart-icon" />
              {t('lp_footer_tagline')}
            </p>
          </div>
        </div>
      </footer>

      {/* Newsletter Popup */}
      <NewsletterPopup isOpen={showNewsletterPopup} onClose={() => setShowNewsletterPopup(false)} />

      {/* Exit Intent Popup */}
      <ExitIntentPopup />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<BlogPage />} />
              <Route path="/blog/:articleId" element={<ArticlePage />} />
              <Route path="/tag/:tagName" element={<TagPage />} />
              <Route path="/penulis/:authorSlug" element={<AuthorPage />} />
              <Route path="/unsubscribe/:subscriberId" element={<UnsubscribePage />} />
              <Route path="/buku" element={<BookLandingPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/redeem" element={<RedeemPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/member" element={<MemberArea />} />
            </Routes>
            <Chatbot />
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
